import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ReturnOptionMethod } from "@redotech/redo-return-core-common/return-flow/return-option";
import {
  SubmitClaimState,
  returnOptionLabels,
} from "@redotech/redo-return-ui/flows/return-flow/submit-claim";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import {
  ProductAutocomplete,
  ProductVariantInfo,
} from "@redotech/redo-web/product-autocomplete";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import { memo, useContext, useMemo } from "react";
import { useFieldEditable } from "../flow-editor/flow-readonly-context";
import { useProductAutocomplete } from "./hooks/use-product-autocomplete";
import { returnOptionMethods } from "./return";

export const SubmitClaimDetails = memo(function Details({
  state,
  setState,
}: StepTypeDetailsProps<SubmitClaimState>) {
  const { t } = useTranslation();
  const team = useContext(TeamContext);
  const hasPermission = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const canEditReturnsSettings = useFieldEditable(hasPermission);
  const canEditMethod = useFieldEditable(hasPermission, "claimMethod");

  const returnOptionMethodsListItems: RedoListItem<
    ReturnOptionMethod["type"]
  >[] = useMemo(
    () =>
      returnOptionMethods.map((method) => ({
        id: method,
        value: method,
        type: "text" as const,
        text: returnOptionLabels(method) || "",
      })),
    [],
  );

  const { productsState, selectedProduct, onSelectedProductChange } =
    useProductAutocomplete(
      team?._id || "",
      state.option.variantExchangeAddOnVariantId,
    );

  const handleActionChange = useHandler((method: ReturnOptionMethod["type"]) =>
    setState((state) =>
      produce(state, (state) => {
        state.option.method = method;
      }),
    ),
  );
  const handleRequireSendBack = useHandler((value: boolean) =>
    setState((state) =>
      produce(state, (state) => {
        state.option.requireSendBack = value;
      }),
    ),
  );
  const handleExchangeShipping = useHandler((value: boolean) =>
    setState((state) =>
      produce(state, (state) => {
        state.option.chargeCustomerForNewOrderShipping = value;
      }),
    ),
  );
  const handleCarryOverLineItemProperties = useHandler((value: boolean) =>
    setState((state) =>
      produce(state, (state) => {
        state.option.carryOverLineItemProperties = value;
      }),
    ),
  );
  const handleCarryOverLineItemPropertiesOnAddOnAccept = useHandler(
    (value: boolean) =>
      setState((state) =>
        produce(state, (state) => {
          state.option.carryOverLineItemPropertiesOnAddOnAccept = value;
        }),
      ),
  );
  const handleVariantExchangeVariantId = useHandler(
    (value: ProductVariantInfo | null | undefined) => {
      onSelectedProductChange(value ?? null);
      if (value) {
        setState((state) =>
          produce(state, (state) => {
            state.option.variantExchangeAddOnVariantId = value.variantId;
          }),
        );
      }
    },
  );
  const handleVariantExchangeAddOnTitle = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.variantExchangeAddOnTitle = value;
      }),
    );
  });
  const handleVariantExchangeAddOnAcceptButton = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.variantExchangeAddOnAcceptButton = value;
      }),
    );
  });
  const handleVariantExchangeAddOnRejectButton = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.option.variantExchangeAddOnRejectButton = value;
      }),
    );
  });

  // Require team context to be available
  if (!team) {
    return null;
  }

  return (
    <>
      <Text fontSize="xs" textColor="secondary">
        {t("Compensation Method")}
      </Text>
      <RedoSingleSelectDropdownInput
        disabled={!canEditMethod}
        options={returnOptionMethodsListItems}
        optionSelected={({ item }) => handleActionChange(item.value)}
        selectedItem={returnOptionMethodsListItems.find(
          (item) => item.value === state.option.method,
        )}
        size="xs"
      />
      <RedoToggle
        disabled={!canEditReturnsSettings}
        label={t("Require customer to send back item?")}
        setValue={handleRequireSendBack}
        value={state.option.requireSendBack || false}
      />
      {state.option.method === ReturnOptionMethod.EXCHANGE && (
        <>
          <RedoToggle
            description={t(
              "If enabled, customer will be charged for outgoing shipping of exchange orders.",
            )}
            disabled={!canEditReturnsSettings}
            label={t("Charge customer for new order shipping")}
            setValue={handleExchangeShipping}
            value={state.option.chargeCustomerForNewOrderShipping || false}
          />
          <RedoToggle
            description={t(
              "If enabled, line item properties (e.g. engravings, monograms) from the returned item are copied to the same-item or variant exchange item.",
            )}
            disabled={!canEditReturnsSettings}
            label={t("Carry over item properties to exchanges")}
            setValue={handleCarryOverLineItemProperties}
            value={state.option.carryOverLineItemProperties ?? true}
          />
          <RedoToggle
            description={t(
              "Offer the customer an add-on product if they do same-item exchange. The customer will be charged the cost of the product",
            )}
            disabled={!canEditReturnsSettings}
            label={t("Same-item exchange add-on")}
            setValue={() => {
              setState((state) =>
                produce(state, (state) => {
                  state.option.variantExchangeAddOn =
                    !state.option.variantExchangeAddOn;
                }),
              );
            }}
            value={state.option.variantExchangeAddOn ?? false}
          />
          {state.option.variantExchangeAddOn && (
            <Flex dir="column" gap="lg" pl="lg">
              <ProductAutocomplete
                disabled={!canEditReturnsSettings}
                label={t("Product to add to exchange")}
                productsState={productsState}
                setValue={handleVariantExchangeVariantId}
                value={selectedProduct}
              />
              <RedoTextInput
                label={t("Title text")}
                setValue={handleVariantExchangeAddOnTitle}
                state={!canEditReturnsSettings ? "disabled" : "default"}
                value={state.option.variantExchangeAddOnTitle ?? ""}
              />
              <Flex dir="row" gap="md">
                <RedoTextInput
                  fullWidth
                  label={t("Accept text")}
                  placeholder={t("Yes")}
                  setValue={handleVariantExchangeAddOnAcceptButton}
                  state={!canEditReturnsSettings ? "disabled" : "default"}
                  value={state.option.variantExchangeAddOnAcceptButton ?? ""}
                />
                <RedoTextInput
                  fullWidth
                  label={t("Reject text")}
                  placeholder={t("No")}
                  setValue={handleVariantExchangeAddOnRejectButton}
                  state={!canEditReturnsSettings ? "disabled" : "default"}
                  value={state.option.variantExchangeAddOnRejectButton ?? ""}
                />
              </Flex>
              {(state.option.carryOverLineItemProperties ?? true) && (
                <RedoToggle
                  description={t(
                    "If enabled, line item properties (e.g. engravings, monograms) are copied only when the customer accepts the add-on. Rejecting the add-on does not copy them.",
                  )}
                  disabled={!canEditReturnsSettings}
                  label={t("Carry over item properties only if they accept")}
                  setValue={handleCarryOverLineItemPropertiesOnAddOnAccept}
                  value={
                    state.option.carryOverLineItemPropertiesOnAddOnAccept ??
                    false
                  }
                />
              )}
            </Flex>
          )}
        </>
      )}
    </>
  );
});
